// START IMPORTS
// Hooks imports
import { useReport } from '@ltvco/refresh-lib/hooks';
import { Box, Stack } from '@ltvco/refresh-lib/theme';
import {
  DateUtil,
  isZeroed,
  nameize,
  useScrollToSectionOnNavigate,
} from '@ltvco/refresh-lib/utils';
import {
  ReportLoading,
  type ReportOptions,
  type ReportMonitor,
  DebugMenu,
} from '@ltvco/refresh-lib/v1';
import {
  ReportFactory,
  type Owner,
  AddressHistorySection,
  AssetsSection,
  AssociatesSection,
  BankruptcySection,
  CourtRecords,
  CourtRecordsLoadingScreen,
  CriminalOrTrafficSection,
  EducationSection,
  EmailSection,
  JobsSection,
  LicensesAndPermitsSection,
  MaritalRecordsSection,
  MonitoringBannerSection,
  NameAncestrySection,
  NeighborsSection,
  NotesSection,
  PersonReportOverview,
  PhoneSection,
  RelatedReportsSection,
  RelativesSection,
  ReportActionsWithDateUpdated,
  ReportChangesOverview,
  ReportClaimingSection,
  ReportNavigationMenu,
  type NavLinkDataV2,
  type ReportNavigationMenuProps,
  ReportNavigationMobileMenu,
  ReportNullState,
  ReportRating,
  SocialSection,
  PdfSection,
  SpouseSection,
  useSession,
} from '@ltvco/refresh-lib/v2';
// Theme and UI imports
// Utility imports
// Core lib v1 imports
// Core lib v2 imports
// Vendor imports
import { useFeatureIsOn, useQueryClient } from '@ltvco/refresh-lib/vendors';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// App-specific imports
import { constants } from 'appConstants';
import { getPersonNavLinkDataV2 } from 'navLinkData/personNavLinkData';

// Type imports
import type { ReportRouteProps } from './types';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface PersonReportProps extends ReportRouteProps {}
// END INTERFACES_AND_TYPES

// START CONSTANTS
const reportType = 'person';
// END CONSTANTS

export function PersonReportPage({
  permalink,
  isMonitored: _unusedInitialMonitored = false,
}: PersonReportProps) {
  // START HOOKS_AND_QUERIES
  const {
    session: { account },
  } = useSession();
  const queryResult = useReport(permalink);
  const { currentMonitors, reportIsMonitored } = queryResult;
  const { hash } = useLocation();
  const isInPartialAddressesVariation = useFeatureIsOn('OAR-1579');
  const isInSpouseSectionVariation = useFeatureIsOn('RFRSH-4363');
  const queryClient = useQueryClient();
  const currentUserInfo = account?.account?.user_info;
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [showCriminalData, setShowCriminalData] = useState(false);
  const [isCourtRecordsLoading, setIsCourtRecordsLoading] = useState(false);
  const [activeClaiming, setActiveClaiming] = useState(false);
  const [disableClaiming, setDisableClaiming] = useState(false);
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const zeroed = isZeroed(queryResult);
  useScrollToSectionOnNavigate(hash, queryResult);

  const alternativeTitle = isInPartialAddressesVariation
    ? 'Possible Locations'
    : undefined;

  const reportOptions: ReportOptions = {
    potentialOwnerIndex: 0,
    showHighConfidenceDataOnly: false,
    higherConfidenceThreshold: constants.config.higherConfidenceThreshold,
  };

  const hideCriminalBJLData = account?.account.user_settings
    .hide_criminal_records as boolean;
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    setDisableClaiming(
      currentMonitors?.some((monitor: ReportMonitor) => {
        return (
          monitor.report_type === 'detailed_person_report' &&
          monitor.is_claimed &&
          permalink !== monitor.permalink
        );
      })
    );
    setActiveClaiming(
      currentMonitors?.some((monitor: ReportMonitor) => {
        return monitor.permalink === permalink && monitor.is_claimed;
      })
    );
  }, [currentMonitors, permalink]);
  // END EFFECTS

  // START EARLY_RETURNS
  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={14} />;
  }

  if (zeroed) {
    return <ReportNullState />;
  }

  const report = ReportFactory.create(queryResult?.data, 'person');

  if (!report) {
    return <ReportLoading menuItems={14} />;
  }

  if (!report?.data?.people?.length) {
    return <ReportNullState />;
  }
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const reportUpgraded = queryResult.data?.meta?.report_upgraded ?? false;
  const revealFreeBonusData = showCriminalData && !isCourtRecordsLoading;

  let showReportClaiming = false;
  if (
    currentUserInfo?.first_name &&
    currentUserInfo.last_name &&
    report.personName.includes(currentUserInfo.first_name) &&
    report.personName.includes(currentUserInfo.last_name)
  ) {
    showReportClaiming = true;
  }

  const MonitoringComponent =
    showReportClaiming && !disableClaiming ? (
      <ReportClaimingSection
        reportPermaLink={permalink}
        reportType={reportType}
        reportIsMonitoredAndClaimed={activeClaiming}
      />
    ) : reportIsMonitored ? null : (
      <MonitoringBannerSection
        reportPermaLink={permalink}
        reportType={reportType}
      />
    );

  const owner = report.getOwner(reportOptions);
  const {
    ownerName,
    nameInfo,
    emails,
    phones,
    educations,
    jobs,
    profiles,
    usernames,
    addresses,
    ownedAssets,
    marital,
    relatives,
    associates,
    neighbors,
    courts,
    bonusData,
  } = owner;

  const licensesCount =
    (bonusData?.licenses?.dea?.length ?? 0) +
    (bonusData?.licenses?.faa?.length ?? 0) +
    (bonusData?.licenses?.professional?.length ?? 0) +
    (bonusData?.licenses?.sporting?.length ?? 0) +
    (bonusData?.licenses?.weapon?.length ?? 0) +
    (bonusData?.filings?.business?.length ?? 0) +
    (bonusData?.filings?.ucc?.length ?? 0);

  const personNavLinkDataV2 = getPersonNavLinkDataV2(
    owner as Owner,
    hideCriminalBJLData,
    revealFreeBonusData,
    relatives || [],
    licensesCount
  );

  document.title = `${report.personName} - NumberGuru`;
  const dob = owner.identity?.dobs?.[0]?.date?.parsed?.year?.toString() ?? '';

  const date = new DateUtil();
  const reportUpdateDate = report.data.meta?.updated_at
    ? date.parseDateFromString(
        report.data.meta?.updated_at,
        'yyyy-MM-dd',
        'yyyy-MM-dd HH:mm:ss ZZZ'
      )
    : '';

  const delayForCourtRecords = 1000;
  // END DATA_PREPARATION

  // START EVENT_HANDLERS
  function handleCourtRecordsLoadingScreen() {
    setIsCourtRecordsLoading(true);
    setShowCriminalData(false);

    queryClient.invalidateQueries({
      queryKey: ['report', permalink],
    });
    queryClient.refetchQueries({
      queryKey: ['report', permalink],
    });

    setTimeout(() => {
      setIsCourtRecordsLoading(false);
      setShowCriminalData(true);
    }, delayForCourtRecords * 7);
  }
  // END EVENT_HANDLERS

  // START RENDER
  return (
    <Box>
      <Stack direction={'row'}>
        <Box
          sx={{
            display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
          }}
        >
          <ReportNavigationMenu
            reportType="Person Report"
            headerTitle={ownerName}
            menuItems={
              personNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
            }
          />
        </Box>
        <Box marginTop={0}>
          {reportIsMonitored && (
            <ReportChangesOverview
              permalink={permalink}
              navLinkData={personNavLinkDataV2 as unknown as NavLinkDataV2[]}
            />
          )}
          <Box
            sx={{
              height: 30,
              backgroundColor: 'background.default',
              position: 'sticky',
              marginBottom: '-10px',
              top: 52,
              zIndex: 10,
              display: { xs: 'none', sm: 'none', md: 'block', lg: 'block' },
            }}
          />

          <ReportActionsWithDateUpdated
            reportType={reportType}
            reportTitle="Person"
            dateUpdated={reportUpdateDate}
            reportUpgraded={reportUpgraded}
          />
          <PersonReportOverview
            reportType={reportType}
            owner={owner as Owner}
            personName={ownerName}
            personOverviewItemsData={{
              hideCriminalBJLData,
              phones,
              emails,
              addresses,
              relatives,
              images: owner!.images,
              profiles,
              usernames,
              jobs,
              educations,
            }}
            dateUpdated={reportUpdateDate}
            reportUpgraded={revealFreeBonusData}
          />

          {MonitoringComponent}

          <PdfSection permalink={permalink} />

          <PhoneSection
            personName={ownerName}
            phoneList={phones}
            permalink={permalink}
          />

          <EmailSection
            personName={ownerName}
            emailList={emails}
            permalink={permalink}
          />

          <AddressHistorySection
            addresses={addresses}
            personName={ownerName}
            permalink={permalink}
            alternateTitle={alternativeTitle}
          />

          <SocialSection
            personName={ownerName}
            profiles={profiles}
            usernames={usernames}
            permalink={permalink}
          />

          <NameAncestrySection
            firstName={nameize(nameInfo?.parsed?.first ?? null)}
            surName={nameize(nameInfo?.parsed?.last ?? null)}
            yearOfBirth={dob}
          />

          {isInSpouseSectionVariation && (
            <SpouseSection
              personName={ownerName}
              relatives={relatives || []}
              permalink={permalink}
              alternateTitle="Possible Spouses"
              hideSectionDescription={false}
            />
          )}

          <RelativesSection
            personName={ownerName}
            relatives={relatives || []}
            permalink={permalink}
          />

          {!isInSpouseSectionVariation && (
            <MaritalRecordsSection
              personName={ownerName}
              marital={marital}
              permalink={permalink}
            />
          )}

          <AssociatesSection
            personName={ownerName}
            associates={associates || []}
            permalink={permalink}
          />

          <NeighborsSection
            personName={ownerName}
            neighbors={neighbors || []}
            permalink={permalink}
          />

          {!hideCriminalBJLData && (
            <>
              {revealFreeBonusData ? (
                <>
                  <CriminalOrTrafficSection
                    criminalOrTrafficData={courts.criminal || []}
                    personName={ownerName}
                  />
                  <BankruptcySection
                    personName={ownerName}
                    bankruptcyData={courts.bankruptcy || []}
                  />
                </>
              ) : (
                <>
                  {isCourtRecordsLoading ? (
                    <CourtRecordsLoadingScreen
                      personName={ownerName}
                      delayDuration={delayForCourtRecords}
                    />
                  ) : (
                    <CourtRecords
                      handleCourtRecordsLoadingScreen={
                        handleCourtRecordsLoadingScreen
                      }
                    />
                  )}
                </>
              )}
            </>
          )}

          <JobsSection
            personName={ownerName}
            jobs={jobs}
            permalink={permalink}
          />

          <EducationSection
            personName={ownerName}
            educations={educations}
            permalink={permalink}
          />

          <LicensesAndPermitsSection
            personName={ownerName}
            bonusData={bonusData}
            licensesAndPermitsCount={licensesCount}
          />

          <AssetsSection
            personName={ownerName}
            ownedAssets={ownedAssets}
            permalink={permalink}
          />

          <NotesSection permalink={permalink} />

          <RelatedReportsSection
            reportType="person"
            personName={ownerName}
            potentialOwners={report.data.people}
            relatives={relatives}
            addresses={addresses}
            phones={phones}
            emails={emails}
            usernames={usernames}
          />

          <ReportRating
            rating={report.data.meta?.rating || null}
            report_type={reportType}
          />
        </Box>
      </Stack>

      <ReportNavigationMobileMenu
        reportType="Person Report"
        headerTitle={ownerName}
        menuItems={
          personNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
        }
        isMobileBannerShowed={false}
      />

      <DebugMenu menuItems={report.data.rawData.debug_menu} />
    </Box>
  );
  // END RENDER
}
